import React, { useEffect, useRef, useState } from "react";
import { IconGlyph } from "./ProfileDashboardUI";

export const DASHBOARD_PERIOD_OPTIONS = [
  { value: "", label: "All time" },
  { value: "TODAY", label: "Today" },
  { value: "THIS_WEEK", label: "This week" },
  { value: "THIS_MONTH", label: "This month" },
  { value: "THIS_QUARTER", label: "This quarter" },
  { value: "SIX_MONTHS", label: "6 months" },
  { value: "TWELVE_MONTHS", label: "12 months" },
  { value: "CURRENT_FY", label: "Current FY" },
  { value: "CUSTOM", label: "Custom date range" },
];

const getTodayDate = () => {
  const now = new Date();
  const offset = now.getTimezoneOffset() * 60 * 1000;
  return new Date(now.getTime() - offset).toISOString().slice(0, 10);
};

const formatDate = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  return date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    timeZone: "Asia/Kolkata",
  });
};

const getPeriodPreview = (period) => {
  const today = new Date();
  const endLabel = formatDate(today);

  if (!period) return "No global date limit";
  if (period === "TODAY") return endLabel;

  const start = new Date(today);

  if (period === "THIS_WEEK") {
    start.setDate(today.getDate() - ((today.getDay() + 6) % 7));
  } else if (period === "THIS_MONTH") {
    start.setDate(1);
  } else if (period === "THIS_QUARTER") {
    start.setMonth(Math.floor(today.getMonth() / 3) * 3, 1);
  } else if (period === "SIX_MONTHS") {
    start.setMonth(today.getMonth() - 6);
  } else if (period === "TWELVE_MONTHS") {
    start.setFullYear(today.getFullYear() - 1);
  } else if (period === "CURRENT_FY") {
    start.setFullYear(today.getFullYear() - (today.getMonth() < 3 ? 1 : 0), 3, 1);
  }

  return `${formatDate(start)} - ${endLabel}`;
};

const DashboardPeriodFilter = ({ value, customRange = {}, onChange, onCustomApply, loading, effectivePeriod = {}, tokens }) => {
  const [showCustomRange, setShowCustomRange] = useState(value === "CUSTOM");
  const [draftRange, setDraftRange] = useState({
    from: customRange.from || "",
    to: customRange.to || "",
  });
  const [rangeError, setRangeError] = useState("");
  const [isPeriodMenuOpen, setIsPeriodMenuOpen] = useState(false);
  const periodMenuRef = useRef(null);
  const startLabel = formatDate(effectivePeriod.from || customRange.from);
  const endLabel = formatDate(effectivePeriod.to || customRange.to);
  const hasCustomRange = value === "CUSTOM" && startLabel && endLabel;
  const rangeLabel = (effectivePeriod.isFiltered || hasCustomRange) && startLabel && endLabel
    ? `${startLabel} - ${endLabel}`
    : "No global date limit applied";
  const selectedOption = DASHBOARD_PERIOD_OPTIONS.find((option) => option.value === value);
  const selectedPeriodLabel = showCustomRange ? "Custom date" : selectedOption?.label || "All time";

  useEffect(() => {
    setShowCustomRange(value === "CUSTOM");
    if (value === "CUSTOM") {
      setDraftRange({ from: customRange.from || "", to: customRange.to || "" });
    }
  }, [customRange.from, customRange.to, value]);

  useEffect(() => {
    const closeMenuOnOutsideClick = (event) => {
      if (periodMenuRef.current && !periodMenuRef.current.contains(event.target)) {
        setIsPeriodMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", closeMenuOnOutsideClick);
    return () => document.removeEventListener("mousedown", closeMenuOnOutsideClick);
  }, []);

  const handlePeriodChange = (period) => {
    setRangeError("");
    if (period === "CUSTOM") {
      setShowCustomRange(true);
      setIsPeriodMenuOpen(true);
      return;
    }

    setShowCustomRange(false);
    setIsPeriodMenuOpen(false);
    onChange(period);
  };

  const handleApplyCustomRange = () => {
    if (!draftRange.from || !draftRange.to) {
      setRangeError("Select both From and To dates.");
      return;
    }

    if (draftRange.from > draftRange.to) {
      setRangeError("From date cannot be after To date.");
      return;
    }

    setRangeError("");
    onCustomApply(draftRange);
    setIsPeriodMenuOpen(false);
  };

  return (
    <section
      className="dashboard-no-print relative z-20 mb-4 overflow-visible rounded-xl border shadow-sm"
      style={{ borderColor: tokens.line, backgroundColor: tokens.card }}
      aria-label="Dashboard period filter"
    >
      <div className="flex flex-col gap-3 px-3 py-3 sm:flex-row sm:items-center sm:justify-between">
        <div ref={periodMenuRef} className="relative w-full sm:w-auto">
          <button
            type="button"
            aria-expanded={isPeriodMenuOpen}
            aria-haspopup="listbox"
            disabled={loading}
            onClick={() => setIsPeriodMenuOpen((current) => !current)}
            className="flex min-h-[52px] w-full min-w-[260px] items-center gap-3 rounded-lg border px-3 text-left transition hover:border-[#FD6600] disabled:cursor-wait disabled:opacity-60 sm:w-[300px]"
            style={{
              borderColor: isPeriodMenuOpen ? tokens.brand : tokens.line,
              backgroundColor: tokens.card,
              boxShadow: isPeriodMenuOpen ? "0 0 0 2px rgba(253, 102, 0, 0.12)" : "none",
            }}
          >
            <span
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg"
              style={{ backgroundColor: "#FFF0E6", color: tokens.brand }}
            >
              <IconGlyph name="calendar" className="h-4 w-4" color={tokens.brand} />
            </span>
            <span className="min-w-0 flex-1">
              <span className="block truncate text-[13px] font-semibold" style={{ color: tokens.ink }}>{selectedPeriodLabel}</span>
              <span className="block truncate text-[11px]" style={{ color: tokens.faint }}>{rangeLabel}</span>
            </span>
            <svg
              className={`h-4 w-4 shrink-0 transition-transform ${isPeriodMenuOpen ? "rotate-180" : ""}`}
              viewBox="0 0 20 20"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              aria-hidden="true"
              style={{ color: tokens.faint }}
            >
              <path d="m5 7.5 5 5 5-5" />
            </svg>
          </button>

          {isPeriodMenuOpen ? (
            <div
              role="listbox"
              aria-label="Dashboard period"
              className="absolute left-0 top-[calc(100%+8px)] z-40 w-full min-w-[300px] overflow-hidden rounded-xl border p-2 shadow-[0_16px_34px_rgba(15,23,42,0.16)]"
              style={{ borderColor: tokens.line, backgroundColor: tokens.card }}
            >
              <p className="px-2.5 py-1.5 text-[10px] font-bold uppercase tracking-[0.14em]" style={{ color: tokens.faint }}>
                Dashboard period
              </p>
              <div className="my-1 border-t" style={{ borderColor: tokens.line }} />
              {DASHBOARD_PERIOD_OPTIONS.map((option) => {
                const isActive = option.value === "CUSTOM" ? showCustomRange : value === option.value && !showCustomRange;
                const isCustomOption = option.value === "CUSTOM";

                return (
                  <React.Fragment key={option.value || "ALL_TIME"}>
                    {isCustomOption ? <div className="my-1 border-t" style={{ borderColor: tokens.line }} /> : null}
                    <button
                      type="button"
                      role="option"
                      aria-selected={isActive}
                      disabled={loading}
                      onClick={() => handlePeriodChange(option.value)}
                      className="flex w-full items-center gap-3 rounded-lg px-2.5 py-2 text-left transition hover:opacity-90 disabled:cursor-wait disabled:opacity-60"
                      style={{ backgroundColor: isActive ? tokens.primaryLight : "transparent" }}
                    >
                      <span
                        className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full border"
                        style={{ borderColor: isActive ? tokens.brand : tokens.lineStrong || tokens.line }}
                      >
                        {isActive ? <span className="h-2 w-2 rounded-full" style={{ backgroundColor: tokens.brand }} /> : null}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block text-[13px] font-semibold" style={{ color: isActive ? tokens.primary : tokens.ink }}>{option.label}</span>
                        <span className="block truncate text-[11px]" style={{ color: tokens.faint }}>
                          {isCustomOption ? "Choose a specific From and To date" : getPeriodPreview(option.value)}
                        </span>
                      </span>
                    </button>
                  </React.Fragment>
                );
              })}

              {showCustomRange ? (
                <div
                  className="mt-1 space-y-2 border-t px-1 pt-2"
                  style={{ borderColor: tokens.line }}
                >
                  <input
                    type="date"
                    aria-label="Custom range start date"
                    value={draftRange.from}
                    max={draftRange.to || getTodayDate()}
                    disabled={loading}
                    onChange={(event) => {
                      setRangeError("");
                      setDraftRange((current) => ({ ...current, from: event.target.value }));
                    }}
                    className="h-9 w-full rounded-lg border px-3 text-xs font-medium outline-none"
                    style={{ borderColor: tokens.line, backgroundColor: tokens.card, color: tokens.ink }}
                  />
                  <input
                    type="date"
                    aria-label="Custom range end date"
                    value={draftRange.to}
                    min={draftRange.from || undefined}
                    max={getTodayDate()}
                    disabled={loading}
                    onChange={(event) => {
                      setRangeError("");
                      setDraftRange((current) => ({ ...current, to: event.target.value }));
                    }}
                    className="h-9 w-full rounded-lg border px-3 text-xs font-medium outline-none"
                    style={{ borderColor: tokens.line, backgroundColor: tokens.card, color: tokens.ink }}
                  />
                  <button
                    type="button"
                    disabled={loading}
                    onClick={handleApplyCustomRange}
                    className="h-9 w-full rounded-lg border px-4 text-xs font-semibold transition disabled:cursor-wait disabled:opacity-60"
                    style={{ borderColor: tokens.ink, backgroundColor: tokens.card, color: tokens.ink }}
                  >
                    Apply
                  </button>
                  {rangeError ? (
                    <p className="text-[11px] font-medium" style={{ color: tokens.danger }}>{rangeError}</p>
                  ) : null}
                </div>
              ) : null}
            </div>
          ) : null}
        </div>

        <p className="text-[11px] font-medium sm:text-right" style={{ color: tokens.faint }}>
          Reporting period: <span style={{ color: tokens.ink }}>{rangeLabel}</span>
        </p>
      </div>

    </section>
  );
};

export default DashboardPeriodFilter;
